import React from 'react';
import { view, i18next, utils } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import controlIds from '../../constants/control-ids';
import appearanceAttributes from '../../constants/appearance-attributes';
import controlUnits from '../../constants/control-units';

const { Paragraph, Icon } = designSystem;

const SLIDER_MIN = 0;
const SLIDER_MAX = 128;
const SLIDER_STEP = 1;

const BORDER_RADIUS_FALLBACK_VALUE = '4';

const DEFAULT_BORDER_TOP_LEFT_RADIUS_CUSTOM_PROPERTY =
  '--ws-media-container-border-top-left-radius';
const DEFAULT_BORDER_TOP_RIGHT_RADIUS_CUSTOM_PROPERTY =
  '--ws-media-container-border-top-right-radius';
const DEFAULT_BORDER_BOTTOM_LEFT_RADIUS_CUSTOM_PROPERTY =
  '--ws-media-container-border-bottom-left-radius';
const DEFAULT_BORDER_BOTTOM_RIGHT_RADIUS_CUSTOM_PROPERTY =
  '--ws-media-container-border-bottom-right-radius';

const ATTRIBUTE_TO_CUSTOM_PROPERTY = {
  [appearanceAttributes.BORDER_TOP_LEFT_RADIUS]: DEFAULT_BORDER_TOP_LEFT_RADIUS_CUSTOM_PROPERTY,
  [appearanceAttributes.BORDER_TOP_RIGHT_RADIUS]: DEFAULT_BORDER_TOP_RIGHT_RADIUS_CUSTOM_PROPERTY,
  [appearanceAttributes.BORDER_BOTTOM_LEFT_RADIUS]:
    DEFAULT_BORDER_BOTTOM_LEFT_RADIUS_CUSTOM_PROPERTY,
  [appearanceAttributes.BORDER_BOTTOM_RIGHT_RADIUS]:
    DEFAULT_BORDER_BOTTOM_RIGHT_RADIUS_CUSTOM_PROPERTY,
};

const CORNERS = {
  topLeft: {
    id: 'top-left-corner',
    attributes: [appearanceAttributes.BORDER_TOP_LEFT_RADIUS],
    iconGlyph: 'corner-top-left-radius',
  },
  topRight: {
    id: 'top-right-corner',
    attributes: [appearanceAttributes.BORDER_TOP_RIGHT_RADIUS],
    iconGlyph: 'corner-top-right-radius',
  },
  bottomLeft: {
    id: 'bottom-left-corner',
    attributes: [appearanceAttributes.BORDER_BOTTOM_LEFT_RADIUS],
    iconGlyph: 'corner-bottom-left-radius',
  },
  bottomRight: {
    id: 'bottom-right-corner',
    attributes: [appearanceAttributes.BORDER_BOTTOM_RIGHT_RADIUS],
    iconGlyph: 'corner-bottom-right-radius',
  },
};

const computeCornersFallback = ({ elementId, attribute }) => {
  const element = view.accessors.getLiveElement(elementId);

  if (!element) return BORDER_RADIUS_FALLBACK_VALUE;

  const defaultValue = getComputedStyle(element).getPropertyValue(
    ATTRIBUTE_TO_CUSTOM_PROPERTY[attribute]
  );

  if (!defaultValue) return BORDER_RADIUS_FALLBACK_VALUE;

  const hasSupportedUnit = Object.values(controlUnits).some((unit) => defaultValue.endsWith(unit));

  if (!hasSupportedUnit) return BORDER_RADIUS_FALLBACK_VALUE;

  const parsedValue = `${parseFloat(defaultValue)}`;

  return utils.isNumeric(parsedValue) ? parsedValue : BORDER_RADIUS_FALLBACK_VALUE;
};

const getPopoverProps = ({ imgSrc, iconGlyph }) => ({
  imgSrc,
  renderContent: () => (
    <Paragraph appearance="white" size="small">
      {i18next.t('Adjust corners for iframe')}. {i18next.t('Click the {icon}', { icon: '' })}{' '}
      <Icon type="white" size={16} strokeWidth={2} glyph={iconGlyph} insideText />
      {i18next.t('{click_the_icon} to adjust each one separately', { click_the_icon: '' })}
    </Paragraph>
  ),
});

const getCorners = (settingId, settings = {}) => ({
  ...settings,
  id: controlIds.CORNERS,
  title: i18next.t('Independent corners'),
  attributes: Object.values(CORNERS).flatMap(({ attributes: attrs }) => attrs),
  computeFallbackValue: computeCornersFallback,
  iconGlyph: 'corner-radius',
  mixedLabel: i18next.t('Mixed'),
  step: SLIDER_STEP,
  min: SLIDER_MIN,
  max: SLIDER_MAX,
  items: Object.values(CORNERS).map((item) => ({
    ...item,
    computeFallbackValue: computeCornersFallback,
  })),
  popover: getPopoverProps({
    iconGlyph: 'corner-radius',
  }),
});

export default getCorners;
